import React, { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Ornament } from "../components/Ornament";

export default function FromTheBook() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    // Persist source for Story Week welcome + user record on signup
    const src = searchParams.get("src") || "book";
    try { localStorage.setItem("tlb_signup_source", src); } catch (e) { /* ignore */ }
  }, [searchParams]);

  return (
    <div className="min-h-screen paper" data-testid="from-the-book-page">
      <header className="max-w-5xl mx-auto px-6 sm:px-10 py-8 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="text-[#B8973A] text-lg" style={{ fontFamily: "Cormorant Garamond, serif" }}>✦</span>
          <span style={{ fontFamily: "Cormorant Garamond, serif", letterSpacing: "0.06em" }}>The Living Book</span>
        </div>
        <button
          onClick={() => navigate("/login")}
          className="label-ui text-[11px] text-[#8C8278] hover:text-[#111D33] transition-colors"
          data-testid="from-book-signin-link"
        >
          I already have an account
        </button>
      </header>

      <main className="max-w-3xl mx-auto px-6 sm:px-10 py-16 sm:py-24">
        <div className="text-center">
          <p className="label-ui text-[11px] text-[#B8973A] mb-8 tracking-[0.3em]" data-testid="from-book-eyebrow">
            FOR THE READER OF <span style={{ fontStyle: "italic" }}>THE LIVING QUESTIONS</span>
          </p>

          <h1
            className="text-4xl sm:text-5xl lg:text-6xl mb-4"
            style={{ fontFamily: "Cormorant Garamond, serif", fontWeight: 300, lineHeight: 1.15, color: "#111D33" }}
            data-testid="from-book-headline"
          >
            You&apos;ve been carrying the questions.
          </h1>
          <p
            className="text-3xl sm:text-4xl lg:text-5xl italic mb-10"
            style={{ fontFamily: "Cormorant Garamond, serif", fontWeight: 300, lineHeight: 1.2, color: "#B8973A" }}
            data-testid="from-book-subheadline"
          >
            Now you can capture the answers.
          </p>

          <Ornament className="my-10" />

          <p
            className="text-[#1B2A4A] italic max-w-2xl mx-auto mb-10"
            style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.35rem", lineHeight: 1.6 }}
            data-testid="from-book-body"
          >
            Thank you for reading. If any of the one hundred conversations
            brought a face to mind, this is the place where their voice
            can be preserved.
          </p>

          <p
            className="text-[#8C8278] max-w-2xl mx-auto mb-12"
            style={{ fontFamily: "Georgia, serif", fontSize: "1.05rem", lineHeight: 1.85 }}
          >
            Seven mornings. Seven questions. Free. On the seventh, your
            answers become the first pages of a Living Book — theirs, or yours.
          </p>

          <button
            onClick={() => navigate("/story-week")}
            className="btn-gold text-base px-10 py-4"
            data-testid="from-book-storyweek-cta"
          >
            Begin Story Week →
          </button>

          <p className="label-ui text-[10px] text-[#8C8278] mt-4">
            Free · No signup · Voice unlocked
          </p>
        </div>

        <Ornament className="my-20" />

        {/* Founding invite — reserved 50 spots for book readers */}
        <div
          className="p-8 sm:p-10 bg-[#F7F0E0]"
          style={{ border: "1px solid #B8973A", borderRadius: 4 }}
          data-testid="from-book-founding-panel"
        >
          <p className="label-ui text-[11px] text-[#B8973A] mb-4 tracking-[0.3em]">
            RESERVED FOR READERS
          </p>
          <h2
            className="text-3xl mb-4"
            style={{ fontFamily: "Cormorant Garamond, serif", color: "#111D33", lineHeight: 1.3 }}
          >
            Fifty Founding Member places.
          </h2>
          <p
            className="text-[#1B2A4A] italic mb-6"
            style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.15rem", lineHeight: 1.6 }}
          >
            As a reader of <span style={{ fontStyle: "italic" }}>The Living Questions</span>,
            the first 50 Founding Member places are £49 a year for one Storyteller, or £89 a year for the whole family (up to four Storytellers). A further 450 places in the beta founding round are £79 and £129.
          </p>
          <p className="label-ui text-[10px] text-[#8C8278] mb-2 tracking-[0.2em]">
            YOUR CODE
          </p>
          <p
            className="mb-6 tracking-[0.3em] text-2xl"
            style={{ fontFamily: "Cormorant Garamond, serif", color: "#B8973A", letterSpacing: "0.3em" }}
            data-testid="from-book-code"
          >
            LIVINGQ25
          </p>
          <p className="text-sm text-[#8C8278]" style={{ fontFamily: "Georgia, serif", lineHeight: 1.7 }}>
            Bring this code with you when payments open. Finish Story Week first if you&apos;d like &mdash; you&rsquo;re welcome either way.
          </p>
        </div>

        {/* The book acknowledgement */}
        <div className="mt-20 text-center" data-testid="from-book-companion-note">
          <p className="label-ui text-[11px] text-[#B8973A] mb-4 tracking-[0.3em]">
            THE COMPANION BOOK
          </p>
          <p
            className="text-[#1B2A4A] italic mb-4"
            style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.15rem" }}
          >
            <span style={{ fontStyle: "italic" }}>The Living Questions</span> — one hundred conversations
            to share, reflect, and preserve.
          </p>
          <p className="label-ui text-[10px] text-[#8C8278]">
            Hardback and paperback, available on Amazon.
          </p>
        </div>

        <p
          className="text-[#B8973A] italic text-center mt-16"
          style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.1rem" }}
          data-testid="from-book-manifesto"
        >
          &ldquo;The greatest inheritance we leave isn&apos;t wealth. It&apos;s being truly known.&rdquo;
        </p>
      </main>
    </div>
  );
}
